import { Badge, Box, Button, Group, Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Page } from '../../components/page';
import {
  deleteReadNotificationsRequest,
  getNotificationsRequest,
  markNotificationsReadRequest,
} from '../../data/notifications';
import { AppNotification } from '../../utils/types/data/services/notification';

const PAGE_SIZE = 20;

const NOTIFICATION_TYPE_LABELS: Record<
  string,
  { label: string; color: string }
> = {
  'lunch-late-return': { label: 'Atraso no retorno', color: 'orange' },
  'lunch-missed-punch': { label: 'Ponto não registrado', color: 'red' },
};

function notificationStyle(type: string) {
  return NOTIFICATION_TYPE_LABELS[type] ?? { label: type, color: 'gray' };
}

export function NotificationsPanel() {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  async function fetchMore(reset = false) {
    setLoading(true);
    try {
      const nextOffset = reset ? 0 : offset;
      const { data } = await getNotificationsRequest({
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setNotifications((prev) => (reset ? data : [...prev, ...data]));
      setOffset(nextOffset + data.length);
      setHasMore(data.length === PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchMore(true);
    markNotificationsReadRequest().catch(() => null);
  }, []);

  async function handleClearRead() {
    setClearing(true);
    try {
      await deleteReadNotificationsRequest();
      setNotifications((prev) => prev.filter((n) => !n.read));
    } finally {
      setClearing(false);
    }
  }

  return (
    <Page title="Painel de Notificações">
      <Group px={16} mb={12} position="right">
        <Button
          size="sm"
          variant="outline"
          color="gray"
          loading={clearing}
          onClick={handleClearRead}
        >
          Limpar lidas
        </Button>
      </Group>

      {notifications.length === 0 && !loading ? (
        <Text color="dimmed" align="center" mt={32}>
          Sem notificações
        </Text>
      ) : (
        <Stack spacing={0} mx={16}>
          {notifications.map((n) => {
            const { label, color } = notificationStyle(n.type);
            return (
              <Box
                key={n.id}
                px={16}
                py={12}
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.colors.gray[2]}`,
                  borderRadius: 4,
                  backgroundColor: n.read ? undefined : theme.colors.blue[0],
                })}
              >
                <Group spacing={8} mb={4}>
                  <Badge color={color} size="sm">
                    {label}
                  </Badge>
                  <Text size="xs" color="dimmed">
                    {new Date(n.createdAt).toLocaleString('pt-BR')}
                  </Text>
                </Group>
                <Text size="sm" fw={500}>
                  {n.title}
                </Text>
                <Text size="sm" color="dimmed">
                  {n.message}
                </Text>
              </Box>
            );
          })}
          {hasMore && (
            <Box py={16} style={{ textAlign: 'center' }}>
              <Button
                variant="subtle"
                loading={loading}
                onClick={() => fetchMore(false)}
              >
                Carregar mais
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Page>
  );
}
